import { useState, useEffect, useRef } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';

export interface ServerData<T> {
  data: T;
  /** Solo il primo caricamento: dai successivi la lista resta a schermo. */
  loading: boolean;
  /** Ricaricamento con dati gia' a schermo (cambio pagina, filtri, refresh). */
  refreshing: boolean;
  error: string | null;
  /**
   * Cresce a ogni risposta arrivata. Chi anima l'entrata delle righe lo mette nella
   * chiave: con "Aggiorna" gli id restano gli stessi e React non rimonterebbe nulla.
   */
  version: number;
}

/**
 * Le liste mostrano lo skeleton per almeno 150ms, anche se i dati arrivano prima: righe
 * e card entrano poi con la loro animazione invece di lampeggiare.
 */
export const LIST_MIN_LOADING_MS = 150;

/**
 * Chiamata e timer partono insieme; si risolve quando sono finiti entrambi. Risposta
 * prima dei `minMs`: si aspetta il timer. Risposta dopo: si consegna appena arriva,
 * senza ritardi aggiunti. Un errore invece passa subito, senza attendere il timer.
 */
export async function withMinDuration<T>(request: Promise<T>, minMs: number): Promise<T> {
  if (minMs <= 0) return request;
  const timer = new Promise<void>((resolve) => setTimeout(resolve, minMs));
  const [data] = await Promise.all([request, timer]);
  return data;
}

/**
 * Esegue `fetcher` a ogni cambio di `key` (query serializzata + refreshKey). Una
 * risposta arrivata dopo una richiesta piu' recente viene scartata: cambiando pagina o
 * filtro in fretta non si vede mai una lista vecchia sopra quella nuova.
 */
export function useServerData<T>(
  key: string,
  initial: T,
  fetcher: () => Promise<T>,
  enabled = true,
  /** Durata minima del caricamento: lo skeleton resta almeno questo, poi entrano le righe. */
  minDurationMs = 0,
  /** Falso quando l'errore lo segnala gia' un'altra chiamata partita insieme. */
  toastOnError = true,
): ServerData<T> {
  const { t } = useI18n();
  const [state, setState] = useState<ServerData<T>>({ data: initial, loading: enabled, refreshing: false, error: null, version: 0 });
  const lastRequest = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) return;
    const requestId = ++lastRequest.current;
    setState((prev) => ({ ...prev, refreshing: !prev.loading, error: null }));

    withMinDuration(fetcherRef.current(), minDurationMs)
      .then((data) => {
        if (requestId !== lastRequest.current) return;
        setState((prev) => ({ data, loading: false, refreshing: false, error: null, version: prev.version + 1 }));
      })
      .catch(() => {
        if (requestId !== lastRequest.current) return;
        const msg = String(t('errors.loadFailed'));
        setState((prev) => ({ ...prev, loading: false, refreshing: false, error: msg }));
        if (toastOnError) toast({ title: String(t('common.error')), description: msg, variant: 'destructive' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return state;
}
